import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { IconPhotoOff } from '@tabler/icons-react';
import { compareReadableLabels } from '../utils/compareReadableLabels.js';
import { cx } from '../utils/cx.js';
import { Input } from './Input.js';
import { Popover } from './Popover.js';
import { ScrollArea } from './ScrollArea.js';
import styles from './SearchableSelect.module.css';
import { focusAdjacent } from '../utils/focusTrap.js';
import { Pressable } from './Pressable.js';

export type SearchableSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Meta / null-bucket rows (e.g. « Sans type ») — muted italic. */
  tone?: 'default' | 'muted';
  /** Optional leading image (brand logo, etc.) for scannable lists. */
  imageUrl?: string | null;
  /**
   * When set, `label` is a muted eyebrow above this primary line
   * (e.g. tarif type + amount).
   */
  detail?: string | null;
};

export type SearchableSelectProps = {
  label?: string;
  hint?: string;
  error?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  searchPlaceholder?: string;
  listAriaLabel?: string;
  id?: string;
  className?: string;
  showErrorText?: boolean;
  /**
   * When true, a non-matching search query can be committed as a custom value
   * (« Utiliser « … » »). Stored value equals the trimmed query string.
   */
  creatable?: boolean;
  /**
   * When false (default), open + option pick run on the click (no 120ms press delay).
   * Pass true only if a deliberate press fill is wanted.
   */
  pressFeedback?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
};

const CREATE_INDEX = -1;
/** Keep the open menu small. Bigger sets stay searchable; only this many rows mount. */
const MAX_RENDERED_OPTIONS = 80;

function OptionLeadingThumb({ url, className }: { url: string | null | undefined; className: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [url]);

  const trimmed = url?.trim() || null;
  if (trimmed && !broken) {
    return (
      <img
        className={className}
        src={trimmed}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <span className={cx(className, styles.optionImageEmpty)} aria-hidden>
      <IconPhotoOff size={14} stroke={1.8} />
    </span>
  );
}

export function SearchableSelect({
  label,
  hint,
  error,
  options,
  value,
  onChange,
  disabled,
  required,
  searchPlaceholder = 'Rechercher…',
  listAriaLabel = 'Options',
  id: idProp,
  className,
  showErrorText = true,
  creatable = false,
  pressFeedback = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: SearchableSelectProps) {
  const autoId = useId();
  const fieldId = idProp ?? autoId;
  const listboxId = `${fieldId}-listbox`;
  const errorId = `${fieldId}-error`;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const typeaheadRef = useRef('');
  const typeaheadTimerRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const listUsesImages = useMemo(
    () => options.some((o) => o.imageUrl !== undefined),
    [options],
  );

  const sortedOptions = useMemo(() => {
    const isEmptyBucket = (o: SearchableSelectOption) =>
      o.tone === 'muted' || o.value === '__empty__';
    const blanks = options.filter((o) => !o.value);
    const empties = options.filter((o) => o.value && isEmptyBucket(o));
    const rest = options.filter((o) => o.value && !isEmptyBucket(o));
    rest.sort((a, b) => compareReadableLabels(a.label, b.label));
    return [...blanks, ...rest, ...empties];
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.detail != null && String(o.detail).toLowerCase().includes(q)),
    );
  }, [sortedOptions, query]);

  const visible = useMemo(() => {
    if (filtered.length <= MAX_RENDERED_OPTIONS) return filtered;
    const slice = filtered.slice(0, MAX_RENDERED_OPTIONS);
    if (!value) return slice;
    if (slice.some((o) => o.value === value)) return slice;
    const selectedOpt = filtered.find((o) => o.value === value);
    if (!selectedOpt) return slice;
    return [selectedOpt, ...slice.slice(0, MAX_RENDERED_OPTIONS - 1)];
  }, [filtered, value]);
  const hiddenCount = filtered.length - visible.length;

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label ?? (value ? value : 'Choisir…');
  const selectedDetail = selected?.detail?.trim() || null;
  const selectedMuted =
    !selected || selected.tone === 'muted' || selected.value === '__empty__' || !selected.value;
  const showSelectedThumb = listUsesImages && Boolean(selected) && !selectedMuted;

  const createQuery = query.trim();
  const showCreateOption =
    creatable &&
    createQuery.length > 0 &&
    !options.some(
      (o) =>
        o.value.toLowerCase() === createQuery.toLowerCase() ||
        o.label.toLowerCase() === createQuery.toLowerCase(),
    );

  const selectableIndices = useMemo(() => {
    const idxs = visible.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    if (showCreateOption) return [CREATE_INDEX, ...idxs];
    return idxs;
  }, [visible, showCreateOption]);

  const commitCreate = () => {
    if (!showCreateOption) return;
    onChange(createQuery);
    setOpen(false);
    setQuery('');
  };

  const pickIndex = (index: number) => {
    if (index === CREATE_INDEX) {
      commitCreate();
      return;
    }
    const opt = visible[index];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const moveActive = (delta: number) => {
    if (!selectableIndices.length) return;
    const currentPos = selectableIndices.indexOf(activeIndex);
    const nextPos =
      currentPos < 0
        ? delta > 0
          ? 0
          : selectableIndices.length - 1
        : (currentPos + delta + selectableIndices.length) % selectableIndices.length;
    setActiveIndex(selectableIndices[nextPos] ?? selectableIndices[0] ?? 0);
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        const selectedIdx = visible.findIndex((o) => o.value === value && !o.disabled);
        setActiveIndex(selectedIdx >= 0 ? selectedIdx : (selectableIndices[0] ?? 0));
      } else if (e.key === 'ArrowDown') {
        moveActive(1);
      } else if (e.key === 'ArrowUp') {
        moveActive(-1);
      }
      return;
    }
    if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      setOpen(true);
      const nextQuery = `${typeaheadRef.current}${e.key}`.toLowerCase();
      typeaheadRef.current = nextQuery;
      if (typeaheadTimerRef.current) window.clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = window.setTimeout(() => {
        typeaheadRef.current = '';
      }, 500);
      const idx = visible.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(nextQuery));
      if (idx >= 0) setActiveIndex(idx);
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(selectableIndices[0] ?? 0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(selectableIndices[selectableIndices.length - 1] ?? 0);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      pickIndex(activeIndex);
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const backwards = e.shiftKey;
      const trigger = triggerRef.current;
      setOpen(false);
      setQuery('');
      if (trigger) queueMicrotask(() => focusAdjacent(trigger, backwards));
    }
  };

  const trigger = (
    <Pressable
      ref={triggerRef}
      id={fieldId}
      type="button"
      className={cx(
        styles.trigger,
        showSelectedThumb && styles.triggerWithImage,
        error && styles.triggerError,
      )}
      disabled={disabled}
      skipPressFeedback={!pressFeedback}
      role="combobox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-label={ariaLabel}
      aria-invalid={!!error}
      aria-required={required || undefined}
      aria-describedby={describedBy}
      aria-haspopup="listbox"
      onKeyDown={handleTriggerKeyDown}
    >
      {showSelectedThumb ? (
        <OptionLeadingThumb url={selected?.imageUrl} className={styles.triggerImage} />
      ) : null}
      <span className={styles.triggerLabel}>
        {selectedDetail ? (
          <span className={styles.optionTextStack}>
            <span className={styles.optionEyebrow}>{selectedLabel}</span>
            <span className={styles.optionDetail}>{selectedDetail}</span>
          </span>
        ) : (
          <span className={!selected ? styles.triggerPlaceholder : undefined}>{selectedLabel}</span>
        )}
      </span>
    </Pressable>
  );

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={fieldId}>
          {label}
        </label>
      ) : null}
      <Popover
        opened={open}
        onChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
        portal
        align="start"
        portalMinWidth={280}
        portalMaxWidth={420}
        portalEstimatedHeight={320}
        portalZIndex="var(--wp-z-popover-over-modal)"
        fullWidth
        toggleOnTargetClick={!disabled}
        pressFeedback={pressFeedback}
        target={trigger}
      >
        <div
          className={styles.panel}
          onKeyDown={(e) => {
            handleListKeyDown(e);
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={`${listAriaLabel} — recherche`}
          />
          <ScrollArea
            className={styles.list}
            maxHeight="15rem"
            fade="y"
            viewportProps={{
              id: listboxId,
              role: 'listbox',
              'aria-label': listAriaLabel,
            }}
          >
            <div className={styles.listInner}>
              {showCreateOption ? (
                <Pressable
                  type="button"
                  role="option"
                  aria-selected={false}
                  skipPressFeedback={!pressFeedback}
                  className={cx(
                    styles.option,
                    open && activeIndex === CREATE_INDEX && styles.optionFocused,
                  )}
                  onMouseEnter={() => setActiveIndex(CREATE_INDEX)}
                  onClick={commitCreate}
                >
                  <span className={styles.optionLabel}>Utiliser « {createQuery} »</span>
                </Pressable>
              ) : null}
              {visible.map((o, index) => {
                const muted = o.tone === 'muted' || o.value === '__empty__' || !o.value;
                const showThumb = listUsesImages && !muted;
                return (
                  <Pressable
                    key={o.value || '__blank__'}
                    type="button"
                    role="option"
                    aria-selected={value === o.value}
                    aria-disabled={o.disabled || undefined}
                    disabled={o.disabled}
                    skipPressFeedback={!pressFeedback}
                    data-empty-bucket={o.value === '__empty__' ? 'true' : undefined}
                    className={cx(
                      styles.option,
                      showThumb && styles.optionWithImage,
                      o.disabled && styles.optionDisabled,
                      value === o.value && styles.optionActive,
                      open && index === activeIndex && styles.optionFocused,
                      muted && styles.optionMuted,
                    )}
                    onMouseEnter={() => !o.disabled && setActiveIndex(index)}
                    onClick={() => {
                      if (o.disabled) return;
                      onChange(o.value);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    {showThumb ? (
                      <OptionLeadingThumb url={o.imageUrl} className={styles.optionImage} />
                    ) : null}
                    {o.detail?.trim() ? (
                      <span className={styles.optionTextStack}>
                        <span className={styles.optionEyebrow}>{o.label}</span>
                        <span className={styles.optionDetail}>{o.detail.trim()}</span>
                      </span>
                    ) : (
                      <span className={styles.optionLabel}>{o.label}</span>
                    )}
                  </Pressable>
                );
              })}
              {filtered.length === 0 && !showCreateOption ? (
                <p className={styles.empty}>Aucun résultat</p>
              ) : hiddenCount > 0 ? (
                <p className={styles.empty}>Affinez la recherche. {hiddenCount} autres.</p>
              ) : null}
            </div>
          </ScrollArea>
        </div>
      </Popover>
      {error && showErrorText ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
